'use client';

import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useCatalogEditing } from '@/hen-ui/catalog/editing-context';
import { EDITOR_HOST_CLASS_PROP } from '@/hen-ui/catalog/editor-host';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';

type ShowMoreInitialState = 'closed' | 'open';
type ShowMoreTriggerPosition = 'start' | 'center' | 'end';
type ShowMoreTriggerWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type ShowMoreTriggerSize = 'b3' | 'b4' | 'b5';

/** Only the exact word `open` starts the list expanded. */
function startsOpen(value: unknown): boolean {
  return value === 'open';
}

const TRIGGER_POSITION_CLASS: Record<ShowMoreTriggerPosition, string> = {
  start: 'self-start',
  center: 'self-center',
  end: 'self-end',
};

const TRIGGER_WEIGHT_CLASS: Record<ShowMoreTriggerWeight, string> = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const TRIGGER_SIZE_CLASS: Record<ShowMoreTriggerSize, string> = {
  b3: 'text-b3 leading-b3',
  b4: 'text-b4 leading-b4',
  b5: 'text-b5 leading-b5',
};

function classFor<K extends string>(
  classes: Record<K, string>,
  value: unknown,
): string | undefined {
  return typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(classes, value)
    ? classes[value as K]
    : undefined;
}

export interface ShowMoreProps {
  /** Items in the list. */
  children?: React.ReactNode;
  /** How many items stay visible before the visitor asks to see the rest. */
  initialVisibleCount?: number;
  /**
   * How the list starts. `open` shows every item first. Anything else
   * starts closed, including a blank or omitted value.
   */
  initialState?: ShowMoreInitialState;
  /** Words on the control while some items are still tucked away. */
  collapsedLabel: string;
  /** Words on the control while every item is showing. */
  expandedLabel: string;
  /** Small arrow beside the words. On unless turned off. */
  showChevron?: boolean;
  /** Runs when the visitor shows or hides the rest of the list. */
  onToggle?: (expanded: boolean) => void;
  /** Where the control sits under the list. Default `start`. */
  triggerPosition?: ShowMoreTriggerPosition;
  /** How heavy the words on the control look. Default `regular`. */
  triggerWeight?: ShowMoreTriggerWeight;
  /** Type step for the words on the control. Default `b5`. */
  triggerSize?: ShowMoreTriggerSize;
}

/**
 * Pulls the editor drop-area class off the props Puck passes to a host slot.
 * An author class name is dropped so spacing stays on the Flex scale.
 */
function partitionEditorProps(props: React.HTMLAttributes<HTMLElement>): {
  domProps: React.HTMLAttributes<HTMLElement>;
  editorHostClassName?: string;
} {
  const record = props as React.HTMLAttributes<HTMLElement> & {
    [EDITOR_HOST_CLASS_PROP]?: unknown;
  };
  const editorHostClassName = record[EDITOR_HOST_CLASS_PROP];
  const { className: _className, ...rest } = record;
  delete rest[EDITOR_HOST_CLASS_PROP];
  return {
    domProps: rest,
    editorHostClassName:
      typeof editorHostClassName === 'string' ? editorHostClassName : undefined,
  };
}

/**
 * Shows the first few items, then a control that reveals the rest in place.
 * On the editor canvas every item is a direct child of the list, with no
 * per-item wrapper, so the editor can see the dropped component.
 */
export const ShowMore = React.forwardRef<HTMLElement, ShowMoreProps>(
  function ShowMore(
    {
      children,
      initialVisibleCount = 3,
      initialState,
      collapsedLabel,
      expandedLabel,
      showChevron = true,
      onToggle,
      triggerPosition,
      triggerWeight,
      triggerSize,
      ...rest
    },
    ref,
  ) {
    const { domProps, editorHostClassName } = partitionEditorProps(rest);
    const editing = useCatalogEditing();
    const [expanded, setExpanded] = React.useState(() =>
      startsOpen(initialState),
    );
    const listId = React.useId();
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const restoreFocus = React.useRef(false);
    const items = React.Children.toArray(children);
    const visibleCount = Math.max(0, initialVisibleCount);
    const hasExtra = !editing && items.length > visibleCount;

    const toggle = () => {
      const next = !expanded;
      restoreFocus.current = true;
      onToggle?.(next);
      setExpanded(next);
    };

    React.useLayoutEffect(() => {
      setExpanded(startsOpen(initialState));
    }, [initialState]);

    React.useLayoutEffect(() => {
      if (!restoreFocus.current) return;
      restoreFocus.current = false;
      triggerRef.current?.focus();
    }, [expanded]);

    const nodes: React.ReactNode[] = editing
      ? items
      : items.map((child, index) => {
          const isCollapsed = !expanded && index >= visibleCount;
          return (
            <Flex
              key={`item-${index}`}
              hidden={isCollapsed}
              className={isCollapsed ? 'hidden' : undefined}
              direction='column'
              gap='none'
              width='full'
            >
              {child}
            </Flex>
          );
        });

    if (hasExtra) {
      const insertAt = expanded
        ? nodes.length
        : Math.min(visibleCount, nodes.length);
      nodes.splice(
        insertAt,
        0,
        <Button
          key='show-more-trigger'
          ref={triggerRef}
          type='button'
          variant='textOnlyLink'
          size='flush'
          className={cn(
            classFor(TRIGGER_POSITION_CLASS, triggerPosition) ??
              TRIGGER_POSITION_CLASS.start,
            classFor(TRIGGER_WEIGHT_CLASS, triggerWeight),
            classFor(TRIGGER_SIZE_CLASS, triggerSize),
          )}
          iconSize={showChevron ? 'sm' : undefined}
          iconAfter={showChevron ? 'chevron-down' : undefined}
          expandedIconAfter={showChevron ? 'chevron-up' : undefined}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={toggle}
        >
          {expanded ? expandedLabel : collapsedLabel}
        </Button>,
      );
    }

    return (
      <Flex
        {...domProps}
        ref={ref}
        id={listId}
        className={editorHostClassName}
        direction='column'
        align='stretch'
        width='full'
        gap='sm'
        data-hen-id={COMPONENT_IDS.ShowMore}
      >
        {nodes}
      </Flex>
    );
  },
);

ShowMore.displayName = 'ShowMore';
